import {
  CheckIcon,
  LocationMarkerIcon,
  RssIcon
} from "@heroicons/react/outline";
import { map } from "lodash";
import React, { useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import CollapsedBox from "../../../components/CollapsedBox";
import BoxIcon from "../../../components/icons/BoxIcon";
import { useBillingPlans } from "../../../context/billingPlans";
import classNames from "../../../utils/classNames";
import { PaymentStep, PlanType } from "../../../utils/types";
import { CurrencyKey } from "../../../utils/useCurrency";

const BaseSubscriptionPlan: React.FC = () => {
  const {
    plans,
    currentPlan,
    setNewPlan,
    newPlan,
    paymentStep,
    setPaymentStep
  } = useBillingPlans();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const history = useHistory();

  useEffect(() => {
    if (paymentStep !== PaymentStep.choosePlan) {
      return;
    }

    containerRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [paymentStep]);

  return (
    <div ref={containerRef}>
      <CollapsedBox
        className="mt-5 lg:mt-8"
        title="Select feature plan"
        icon="1."
        opened={paymentStep === PaymentStep.choosePlan}
        saveButtonName="Continue"
        cancelButtonName="Compare plans"
        setOpened={() => setPaymentStep(PaymentStep.choosePlan)}
        onClickButton={(type) => {
          if (type === "save") {
            setPaymentStep(PaymentStep.featuresByDemand);

            return;
          }

          setPaymentStep(PaymentStep.plans);
          history.push("/pricing");
        }}
      >
        <div className="flex flex-col w-full">
          {map(plans, (plan) => (
            <div
              key={plan.uuid}
              className={classNames(
                plan.type !== PlanType.community
                  ? "dark:hover:bg-gray-700 cursor-pointer"
                  : "opacity-50 cursor-default",
                "flex justify-between p-3 sm:p-5 border-b dark:border-gray-500 last:border-none"
              )}
              onClick={() => {
                if (plan.type === PlanType.community) return;

                setNewPlan((prevState) => ({ ...prevState, plan }));
              }}
            >
              <div className="flex flex-col w-9/12">
                <div className="flex items-center">
                  <div className="relative">
                    <input
                      type="checkbox"
                      onChange={() => {}}
                      className="dark:bg-white bg-gray-200 text-white flex transition duration-150 ease-in-out sm:text-sm sm:leading-5 focus:outline-none h-4 w-4 self-center cursor-pointer checked:bg-hotspotty-400 dark:checked:bg-hotspotty-400 appearance-none rounded"
                      checked={newPlan.plan?.type === plan.type}
                    />
                    <div className="absolute text-white -mt-4 cursor-pointer">
                      {newPlan.plan?.type === plan.type && (
                        <CheckIcon className="h-4 w-4" />
                      )}
                    </div>
                  </div>

                  <span className="text-sm sm:text-lg font-semibold mx-2 sm:mx-3">
                    {plan.name}
                  </span>
                  {plan.name === currentPlan?.name && (
                    <span className="py-1 px-4 font-bold text-center text-xs dark:text-yellow-400 rounded-full dark:bg-gray-700">
                      Current Plan
                    </span>
                  )}
                </div>
                {plan.description && (
                  <span
                    dangerouslySetInnerHTML={{ __html: plan.description }}
                    className="text-xs sm:text-sm my-1 dark:text-gray-200"
                  />
                )}
                <div className="flex flex-col sm:flex-row mt-2">
                  <span className="flex items-center text-xs">
                    <BoxIcon className="w-4 mr-0.5" />
                    {plan.hotspots} hotspots
                  </span>
                  <span className="flex items-center text-xs mx-0 my-1 sm:mx-2 sm:my-0">
                    <LocationMarkerIcon className="w-4 mr-0.5" />
                    {plan.type === PlanType.community ? 2 : 0} locations
                  </span>
                  <span className="flex items-center text-xs">
                    <RssIcon className="w-4 mr-0.5" />
                    {plan.simulations} super simulations per month
                  </span>
                </div>
              </div>
              <div className="flex flex-col items-end w-3/12">
                {plan?.price !== 0 ? (
                  <div>
                    <span className="text-xs sm:text-sm uppercase dark:text-gray-200">
                      starts at
                    </span>
                    <div className="flex items-center mt-1">
                      <span className="sm:text-lg font-bold">
                        {((plan.price || 0) / 100).toLocaleString("en-US", {
                          style: "currency",
                          currency: CurrencyKey.usd,
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        })}
                      </span>
                      <span className="text-sm dark:text-gray-200 ml-1">
                        /month
                      </span>
                    </div>
                  </div>
                ) : (
                  <span className="sm:text-lg font-bold mt-1">FREE</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </CollapsedBox>
    </div>
  );
};

export default BaseSubscriptionPlan;
